import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { creditos, gramos, iso } from "../lib/formato";
import { Aviso, Cabecera, Cargando, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * Los días que el club ha abierto.
 *
 * Es la pregunta de «¿qué días abrimos el mes pasado?», que hasta ahora solo se
 * podía contestar cruzando a ojo la caja, los accesos y las entregas.
 *
 * Salen también los días **cerrados**. Un mes con huecos no dice si el club
 * cerró o si el dato falta; con todos los días delante, un día cerrado se ve
 * como lo que es.
 */

interface Dia {
  fecha: string;
  abierto: boolean;
  socios: number;
  entradas: number;
  entregas: number;
  dispensadoCentimos: number;
  dispensadoMg: number;
  barraCentimos: number;
  barraTickets: number;
  cuotasCentimos: number;
  altas: number;
  cajas: number;
  cajasSinCerrar: number;
  descuadreCentimos: number;
  quien: string[];
  totalCentimos: number;
}

interface Respuesta {
  dias: Dia[];
  resumen: {
    diasAbiertos: number;
    diasEnElRango: number;
    totalCentimos: number;
    sociosDistintos: number;
    diasSinCerrarCaja: number;
  };
}

export default function Dias() {
  const { t, cuenta } = useIdioma();

  /** «2026-09-06» → «sábado 6». El mes ya está en la cabecera del rango. */
  const comoSeDice = (clave: string) => {
    const [anio, mes, dia] = clave.split("-").map(Number);
    const f = new Date(anio!, mes! - 1, dia!);
    return t("di.diaYNumero", { dia: t(`semana.${f.getDay()}` as Clave).toLowerCase(), numero: dia! });
  };

  const primeroDeMes = new Date();
  primeroDeMes.setDate(1);

  const [desde, setDesde] = useState(iso(primeroDeMes));
  const [hasta, setHasta] = useState(iso(new Date()));
  const [soloAbiertos, setSoloAbiertos] = useState(true);

  const datos = useQuery({
    queryKey: ["dias", desde, hasta],
    queryFn: () => api<Respuesta>("/informes/dias", { query: { desde, hasta } }),
  });

  if (datos.isLoading) return <Cargando />;
  const todos = datos.data?.dias ?? [];
  const filas = soloAbiertos ? todos.filter((d) => d.abierto) : todos;
  const resumen = datos.data?.resumen;

  return (
    <>
      <Cabecera titulo={t("di.titulo")} descripcion={t("di.descripcion")}>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ width: 160 }} />
        <span className="suave">{t("di.a")}</span>
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ width: 160 }} />
      </Cabecera>

      {resumen && resumen.diasSinCerrarCaja > 0 && (
        <Aviso tipo="atencion">
          {cuenta(resumen.diasSinCerrarCaja, "di.unDiaSinCerrar", "di.diasSinCerrar")}{" "}
          {t("di.arqueoNoCuadra")} <Link to="/caja">{t("di.irACaja")}</Link>
        </Aviso>
      )}

      <div className="rejilla tres" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={t("di.diasAbiertos")}
          valor={t("di.deTantos", {
            abiertos: resumen?.diasAbiertos ?? 0,
            total: resumen?.diasEnElRango ?? 0,
          })}
          pie={t("di.delPeriodo")}
        />
        <TarjetaDato
          etiqueta={t("di.entradoEnTotal")}
          valor={creditos(resumen?.totalCentimos ?? 0)}
          pie={t("di.entregasBarraCuotas")}
        />
        <TarjetaDato
          etiqueta={t("di.sociosDistintos")}
          valor={String(resumen?.sociosDistintos ?? 0)}
          pie={t("di.hanPasado")}
        />
      </div>

      <Panel sinCuerpo>
        <div className="filtros-gastos">
          <label className="casilla">
            <input
              type="checkbox"
              checked={soloAbiertos}
              onChange={(e) => setSoloAbiertos(e.target.checked)}
            />
            <span>{t("di.soloAbiertos")}</span>
          </label>
          <span className="suave pequeno">{t("di.cuandoCuentaAbierto")}</span>
        </div>

        {filas.length === 0 ? (
          <Vacio>
            {soloAbiertos ? t("di.noAbrioNinguno") : t("di.sinDias")}
          </Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("di.dia")}</th>
                  <th className="num">{t("di.socios")}</th>
                  <th className="num">{t("di.entregas")}</th>
                  <th className="num">{t("di.dispensado")}</th>
                  <th className="num">{t("di.barra")}</th>
                  <th className="num">{t("di.cuotas")}</th>
                  <th className="num">{t("di.total")}</th>
                  <th>{t("di.caja")}</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((d) => (
                  <tr key={d.fecha} className={d.abierto ? undefined : "anulada"}>
                    <td>
                      <strong>{comoSeDice(d.fecha)}</strong>
                      {d.quien.length > 0 && <div className="suave pequeno">{d.quien.join(", ")}</div>}
                      {!d.abierto && <div className="suave pequeno">{t("di.cerrado")}</div>}
                    </td>
                    <td className="num tabular">{d.abierto ? d.socios : "—"}</td>
                    <td className="num tabular">
                      {d.entregas || "—"}
                      {d.altas > 0 && (
                        <div className="suave pequeno">{cuenta(d.altas, "di.unAlta", "di.altas")}</div>
                      )}
                    </td>
                    <td className="num tabular">
                      {d.dispensadoCentimos ? creditos(d.dispensadoCentimos) : "—"}
                      {d.dispensadoMg > 0 && <div className="suave pequeno">{gramos(d.dispensadoMg)}</div>}
                    </td>
                    <td className="num tabular">{d.barraCentimos ? creditos(d.barraCentimos) : "—"}</td>
                    <td className="num tabular">{d.cuotasCentimos ? creditos(d.cuotasCentimos) : "—"}</td>
                    <td className="num tabular">
                      <strong>{d.totalCentimos ? creditos(d.totalCentimos) : "—"}</strong>
                    </td>
                    <td className="pequeno">
                      {d.cajas === 0 ? (
                        <span className="suave">{t("di.sinCaja")}</span>
                      ) : d.cajasSinCerrar > 0 ? (
                        <span style={{ color: "var(--ambar)" }}>{t("di.sinCerrar")}</span>
                      ) : d.descuadreCentimos === 0 ? (
                        <span className="suave">{t("di.cuadro")}</span>
                      ) : (
                        <strong style={{ color: d.descuadreCentimos < 0 ? "var(--rojo)" : "var(--verde)" }}>
                          {d.descuadreCentimos > 0 ? "+" : ""}
                          {creditos(d.descuadreCentimos)}
                        </strong>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <p className="suave pequeno" style={{ marginTop: 12 }}>
        {t("di.notaTotal")}
      </p>
    </>
  );
}
